/**
 * Selector that packages the live tournament/hand state into the shape
 * the table components want. Kept small so routes can compose it
 * without reaching into the store internals.
 */
import { useMemo } from 'react';
import { codenameForPlayerId } from '@/characters/definitions';
import type { OpponentDescriptor } from '@/components/player/OpponentRing';
import { type DealerSeat, resolveDealerSeat } from '@/components/table/DealerPuck';
import type { Card, PlayerId } from '@/engine/types';
import { useGameStore } from '@/state/gameStore';
import { computePot } from '@/utils/pot';

export type TableView = {
  level: number;
  blinds: { sb: number; bb: number; ante: number };
  handNumber: number;
  pot: number;
  board: ReadonlyArray<Card>;
  pendingActor: PlayerId | null;
  heroStack: number;
  heroHoleCards?: readonly [Card, Card];
  heroStreetContribution: number;
  isHeroTurn: boolean;
  /** True when the hero's seat is on the dealer button this hand. */
  heroIsButton: boolean;
  /** True when the hero has been marked eliminated in the tournament. */
  heroEliminated: boolean;
  /** Which seat the DealerPuck should render at, null = pre/between hand. */
  dealerSeat: DealerSeat | null;
  opponents: ReadonlyArray<OpponentDescriptor>;
  /** Parallel to `opponents[]`, giving each slot's underlying engine playerId. */
  opponentPlayerIds: ReadonlyArray<PlayerId>;
};

const EMPTY_VIEW: TableView = {
  level: 0,
  blinds: { sb: 0, bb: 0, ante: 0 },
  handNumber: 0,
  pot: 0,
  board: [],
  pendingActor: null,
  heroStack: 0,
  heroStreetContribution: 0,
  isHeroTurn: false,
  heroIsButton: false,
  heroEliminated: false,
  dealerSeat: null,
  opponents: [],
  opponentPlayerIds: [],
};

export function useTableView(heroId: PlayerId): TableView {
  const tournament = useGameStore((s) => s.tournament);
  const hand = useGameStore((s) => s.hand);
  return useMemo(() => {
    if (!tournament || !hand) {
      // Between-hands (hand === null, tournament still live): surface
      // opponent seats from the tournament roster so the felt layout doesn't
      // visually collapse while showing the showdown summary. Stale bet/street
      // fields are zeroed — the reducer has already cleared per-hand state.
      if (tournament) {
        const heroInTourney = tournament.players.find((p) => p.id === heroId);
        const tourneyOpponents = [...tournament.players]
          .filter((p) => p.id !== heroId)
          .sort((a, b) => a.seat - b.seat);
        return {
          ...EMPTY_VIEW,
          level: tournament.currentLevel.level,
          blinds: {
            sb: tournament.currentLevel.sb,
            bb: tournament.currentLevel.bb,
            ante: tournament.currentLevel.ante,
          },
          handNumber: tournament.handNumber,
          heroEliminated: heroInTourney?.eliminated ?? false,
          heroStack: heroInTourney?.stack ?? 0,
          opponents: tourneyOpponents.map((p) => ({
            codename: codenameForPlayerId(p.id),
            stack: p.stack,
            isTurn: false,
            isFolded: false,
            isAllIn: false,
            isEliminated: p.eliminated,
            inHand: false,
            streetContribution: 0,
          })),
          opponentPlayerIds: tourneyOpponents.map((p) => p.id),
        };
      }
      return EMPTY_VIEW;
    }
    const hero = hand.players.find((p) => p.id === heroId);
    const heroInTourney = tournament.players.find((p) => p.id === heroId);
    const orderedOpponents = [...hand.players]
      .filter((p) => p.id !== heroId)
      .sort((a, b) => a.seat - b.seat);
    return {
      level: hand.level.level,
      blinds: { sb: hand.level.sb, bb: hand.level.bb, ante: hand.level.ante },
      handNumber: hand.handNumber,
      pot: computePot(hand),
      board: hand.board,
      pendingActor: hand.pendingActor,
      heroStack: hero?.stack ?? 0,
      heroHoleCards: hero?.holeCards,
      heroStreetContribution: hero?.streetContribution ?? 0,
      isHeroTurn: hand.pendingActor === heroId,
      heroIsButton: hero?.seat === hand.button,
      heroEliminated: heroInTourney?.eliminated ?? false,
      dealerSeat: resolveDealerSeat(hero?.seat ?? null, hand.button, orderedOpponents),
      opponents: orderedOpponents.map((p) => ({
        codename: codenameForPlayerId(p.id),
        stack: p.stack,
        isTurn: hand.pendingActor === p.id,
        isFolded: p.folded,
        isAllIn: p.allIn,
        isEliminated: p.eliminated,
        inHand: !p.folded && !p.eliminated,
        streetContribution: p.streetContribution,
      })),
      opponentPlayerIds: orderedOpponents.map((p) => p.id),
    };
  }, [tournament, hand, heroId]);
}
